import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router";
import { useId, useState } from "react";
import { useSearchParam } from "@/lib/use-search-param";
import {
  ArrowLeft,
  FileDown,
  Mail,
  RefreshCw,
  Save,
  Upload,
  UserX,
} from "lucide-react";
import { toast } from "sonner";
import { apiGet, apiPatch, apiPost, queryClient } from "@/lib/api";
import { useAccountant } from "@/lib/auth";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { ReinviteDialog } from "@/components/shared/reinvite-dialog";
import { ExportDialog } from "@/components/shared/export-dialog";
import {
  fiscalYearFromConfig,
  fiscalYearLabel,
  fiscalYearNoun,
  fiscalYearStart,
  resolvePeriod,
  type PeriodPreset,
} from "@/lib/period-presets";
import {
  countryConfigOrDefault,
  entityTypeLabel,
  entityTypes,
  isAustralia,
  type CountryConfig,
} from "@/lib/country";
import { ReminderDialog } from "@/components/shared/reminder-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ConnectionBadge,
  SubscriptionBadge,
  type ConnectionStatus,
  type CommissionStatus,
  type SubscriptionStatus,
} from "@/components/shared/status-badge";
import { Skeleton } from "@/components/ui/skeleton";
import { QueryError } from "@/components/shared/query-error";
import { ReceiptTray } from "./receipt-tray";
import type { UploadedFile } from "./bulk-upload";
import { ConfirmedReceiptsTable } from "./confirmed-receipts-table";
import { MessagesTab } from "./messages-tab";
import { BulkUpload, useUploadFile } from "./bulk-upload";

interface ClientDetailData {
  id: string;
  invite_email: string;
  display_name: string;
  entity_type: string;
  bas_frequency: string;
  status: ConnectionStatus;
  subscription_status: SubscriptionStatus;
  commission_status: CommissionStatus;
  receipts_count: number;
  receipts_summary: {
    total_confirmed: number;
    current_fy_confirmed: number;
    total_unprocessed: number;
    total_processed_unconfirmed: number;
  };
  subscription_plan_type?: string;
  invited_at: string | null;
  accepted_at: string | null;
  subscription_started_at: string | null;
  subscription_ended_at: string | null;
  subscription_cancelled_at: string | null;
  subscription_expires_at: string | null;
  /** The client's own country, or their accountant's if they have not
   *  accepted the invite yet. Drives every tax/fiscal-year label below. */
  country?: string;
  country_config?: CountryConfig;
}

const BAS_OPTIONS = [
  { label: "None", value: "none" },
  { label: "Annual", value: "annual" },
  { label: "Quarterly", value: "quarterly" },
];

// Entity types where BAS applies
const BAS_APPLICABLE_ENTITIES = [
  "sole_trader",
  "pty_ltd",
  "partnership",
  "trust",
];

/**
 * The client's current tax year, named the way their country names it: "FY26"
 * in Australia, "2026/27" in the UK, "2026" where the tax year is the calendar
 * year. Windows come from the same helper the receipts table and exports use,
 * and match what the backend counted for `current_fy_confirmed`.
 */
function getCurrentFY(config: CountryConfig | null | undefined): {
  label: string;
  start: string;
  end: string;
  noun: string;
} {
  const fiscalYear = fiscalYearFromConfig(config);
  const today = new Date();
  const period = resolvePeriod("this_fy", { today, fiscalYear });
  const [start, end] = period.caption.split(" to ");
  return {
    label: `Tax return ${fiscalYearLabel(fiscalYearStart(today, fiscalYear), fiscalYear)}`,
    start,
    end,
    noun: fiscalYearNoun(fiscalYear),
  };
}

/** Current BAS quarter. AU-only — BAS has no equivalent elsewhere. */
function getCurrentQuarterInfo(config: CountryConfig | null | undefined): {
  label: string;
  start: string;
  end: string;
} {
  const period = resolvePeriod("this_q", {
    fiscalYear: fiscalYearFromConfig(config),
  });
  const [start, end] = period.caption.split(" to ");
  // "This quarter (Q1 Jul-Sep)" becomes "Q1 BAS (Jul-Sep)"
  const inner = period.label.replace(/^This quarter \(|\)$/g, "");
  const [quarter, months] = inner.split(" ");
  return { label: `${quarter} BAS (${months})`, start, end };
}

// ── Main component ─────────────────────────────────────────────────────────────

export function ClientDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: accountant } = useAccountant();
  const [showUpload, setShowUpload] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<UploadedFile[]>([]);
  // One shared uploader for both the drop zone and the per-row Retry button
  // in the tray. Keeps the state-update path identical in both places.
  const uploadFile = useUploadFile(id ?? "", setUploadFiles);
  const [activeTab, setActiveTab] = useSearchParam("tab", "receipts");
  const [reminderOpen, setReminderOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [confirmKind, setConfirmKind] = useState<
    "disconnect" | "reinvite" | null
  >(null);
  // S18: track the receipts table's active period so opening Export
  // pre-selects the same window.
  const [receiptsPeriod, setReceiptsPeriod] = useState<PeriodPreset>("all");
  const [receiptsFrom, setReceiptsFrom] = useState<string | null>(null);
  const [receiptsTo, setReceiptsTo] = useState<string | null>(null);

  const [settingsForm, setSettingsForm] = useState({
    display_name: "",
    entity_type: "",
    bas_frequency: "",
  });
  // formSeeded + lastLoadedClientId: gate the settings-tab skeleton until the
  // form has been seeded with real server values for the current client.
  const [formSeeded, setFormSeeded] = useState(false);
  const [lastLoadedClientId, setLastLoadedClientId] = useState<string | null>(null);
  const displayNameId = useId();
  const entityTypeLabelId = useId();
  const basFrequencyLabelId = useId();

  const {
    data: client,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["accountant-user", id],
    queryFn: () =>
      apiGet<ClientDetailData>(`/api/accountant/users/${id}`),
    enabled: !!id,
  });

  // Seed the settings form when client data arrives or when the user navigates
  // to a different client. Uses the render-body pattern (same as review.tsx and
  // confirmed-receipts-table.tsx) rather than useEffect to avoid triggering the
  // react-hooks/set-state-in-effect lint rule. formSeeded gates the skeleton so
  // the settings tab shows placeholder rows until the form has real values.
  if (client && client.id !== lastLoadedClientId) {
    setLastLoadedClientId(client.id);
    setSettingsForm({
      display_name: client.display_name || "",
      entity_type: client.entity_type || "",
      bas_frequency: client.bas_frequency || "",
    });
    setFormSeeded(true);
  }

  const updateSettingsMutation = useMutation({
    mutationFn: () => apiPatch(`/api/accountant/users/${id}`, settingsForm),
    onSuccess: () => {
      toast.success("Changes saved");
      queryClient.invalidateQueries({ queryKey: ["accountant-user", id] });
      queryClient.invalidateQueries({ queryKey: ["accountant-users"] });
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Could not save"),
  });

  const reinviteMutation = useMutation({
    mutationFn: ({
      personal_note = "",
      subject,
    }: {
      personal_note?: string;
      subject?: string;
    }) =>
      apiPost(`/api/accountant/users/${id}/reinvite`, {
        personal_note,
        subject,
      }),
    onSuccess: () => {
      toast.success("Invite resent");
      queryClient.invalidateQueries({ queryKey: ["accountant-user", id] });
      queryClient.invalidateQueries({ queryKey: ["accountant-users"] });
      queryClient.invalidateQueries({
        queryKey: ["accountant-messages", id],
      });
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Could not re-invite"),
  });

  const disconnectMutation = useMutation({
    mutationFn: () => apiPost(`/api/accountant/users/${id}/disconnect`),
    onSuccess: () => {
      toast.success("Client disconnected");
      queryClient.invalidateQueries({ queryKey: ["accountant-users"] });
      navigate("/accountant/dashboard");
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Could not disconnect"),
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        {/* Header skeleton */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <Skeleton className="size-9 rounded-lg" />
            <div>
              <div className="flex items-center gap-2">
                <Skeleton className="h-6 w-48" />
                <Skeleton className="h-5 w-16 rounded-full" />
              </div>
              <Skeleton className="h-4 w-32 mt-1" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Skeleton className="h-9 w-28" />
            <Skeleton className="h-9 w-20" />
            <Skeleton className="h-9 w-32" />
          </div>
        </div>
        {/* Two-column skeleton */}
        <div className="flex gap-6">
          <div className="flex-1 min-w-0">
            <Skeleton className="h-9 w-48" />
            <div className="mt-4 space-y-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-14 w-full rounded-lg" />
              ))}
            </div>
          </div>
          <aside className="hidden xl:block w-72 shrink-0 space-y-4">
            <Card>
              <CardHeader className="pb-2">
                <Skeleton className="h-3 w-24" />
              </CardHeader>
              <CardContent className="space-y-2.5">
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-16" />
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <Skeleton className="h-3 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-16 w-full rounded-lg" />
              </CardContent>
            </Card>
          </aside>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            aria-label="Back to clients"
            onClick={() => navigate("/accountant/dashboard")}
          >
            <ArrowLeft className="size-4" aria-hidden />
          </Button>
          <h1 className="text-xl font-semibold">Client</h1>
        </div>
        <QueryError
          error={error}
          message={
            error instanceof Error
              ? error.message
              : "Could not load client details"
          }
          onRetry={() =>
            queryClient.invalidateQueries({ queryKey: ["accountant-user", id] })
          }
        />
      </div>
    );
  }

  if (!client) {
    return (
      <div className="py-12 text-center text-muted-foreground">
        Client not found
      </div>
    );
  }

  const clientName = client.display_name || client.invite_email;

  const countryConfig = countryConfigOrDefault(client.country_config);
  const fy = getCurrentFY(countryConfig);
  const currentQuarterInfo = getCurrentQuarterInfo(countryConfig);
  // BAS is an Australian filing with no equivalent elsewhere, so it is hidden
  // outside AU rather than renamed into something that does not exist.
  const showBAS =
    isAustralia(countryConfig) &&
    !!client.bas_frequency &&
    client.bas_frequency !== "none" &&
    BAS_APPLICABLE_ENTITIES.includes(client.entity_type);

  // Show only confirmed receipts — this is what the accountant has vetted
  // and what the receipts table's "X confirmed" summary also counts. Mixing
  // in processing/unconfirmed draft receipts caused the sidebar and the
  // table to disagree (see PM feedback).
  const confirmedReceiptCount = client.receipts_summary.total_confirmed ?? 0;
  const currentFYReceiptCount =
    client.receipts_summary.current_fy_confirmed ?? 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            aria-label="Back to clients"
            onClick={() => navigate("/accountant/dashboard")}
          >
            <ArrowLeft className="size-4" aria-hidden />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-semibold">{clientName}</h1>
              <ConnectionBadge status={client.status} />
              {client.status === "accepted" && (
                <SubscriptionBadge
                  status={client.subscription_status}
                  expiresAt={client.subscription_expires_at}
                />
              )}
              {/* D2d — receipt count badge in header */}
              {confirmedReceiptCount > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {confirmedReceiptCount} receipts
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              <p className="text-sm text-muted-foreground">
                {client.invite_email}
              </p>
              {client.entity_type && (
                <Badge variant="outline" className="text-xs">
                  {entityTypeLabel(countryConfig, client.entity_type)}
                </Badge>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {client.status === "accepted" && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setReminderOpen(true)}
              >
                <Mail className="mr-1.5 size-4" />
                Send reminder
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setExportOpen(true)}
              >
                <FileDown className="mr-1.5 size-4" />
                Export
              </Button>
              {/* D2b — single upload button: top-right only */}
              <Button size="sm" onClick={() => setShowUpload(!showUpload)}>
                <Upload className="mr-1.5 size-4" />
                Upload receipts
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setConfirmKind("disconnect")}
              >
                <UserX className="mr-1.5 size-4" />
                Disconnect
              </Button>
            </>
          )}
          {(client.status === "declined" ||
            client.status === "disconnected") && (
            <Button size="sm" onClick={() => setConfirmKind("reinvite")}>
              <RefreshCw className="mr-1.5 size-4" />
              Re-invite
            </Button>
          )}
        </div>
      </div>

      {/* Bulk upload panel */}
      {showUpload && client.status === "accepted" && (
        <BulkUpload
          userId={id!}
          setFiles={setUploadFiles}
          uploadFile={uploadFile}
        />
      )}

      {/* State-aware content */}
      {client.status === "invited" && (
        <Card>
          <CardContent className="py-12 text-center">
            <Mail className="mx-auto size-12 text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-semibold">Invitation pending</h3>
            <p className="text-muted-foreground mt-1 max-w-sm mx-auto">
              Invite sent to {client.invite_email}. They'll appear here once
              they accept.
            </p>
            <div className="mt-6 flex justify-center">
              <Button
                variant="outline"
                onClick={() => setConfirmKind("reinvite")}
                disabled={reinviteMutation.isPending}
              >
                <Mail className="mr-1.5 size-4" />
                {reinviteMutation.isPending ? "Resending..." : "Resend invite"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {client.status === "declined" && (
        <Card>
          <CardContent className="py-12 text-center">
            <h3 className="text-lg font-semibold text-destructive">
              Invitation declined
            </h3>
            <p className="text-muted-foreground mt-1 max-w-sm mx-auto">
              {client.invite_email} declined your invitation. You can send a new
              one.
            </p>
            <div className="mt-6">
              <Button onClick={() => setConfirmKind("reinvite")}>
                <RefreshCw className="mr-1.5 size-4" />
                Re-invite
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {client.status === "disconnected" && (
        <Card>
          <CardContent className="py-12 text-center">
            <h3 className="text-lg font-semibold">Client disconnected</h3>
            <p className="text-muted-foreground mt-1 max-w-sm mx-auto">
              This client is no longer connected to your practice. Their
              receipts are still theirs. Re-invite to reconnect.
            </p>
            <div className="mt-6">
              <Button onClick={() => setConfirmKind("reinvite")}>
                <RefreshCw className="mr-1.5 size-4" />
                Re-invite
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {client.status === "accepted" && (
        <div className="flex gap-6">
          {/* Main content */}
          <div className="flex-1 min-w-0">
            {/* D2a — removed "BAS & Tax" tab; D2e — removed "Activity" tab (only milestone events, not real activity) */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="receipts">Receipts</TabsTrigger>
                <TabsTrigger value="messages">Messages</TabsTrigger>
                <TabsTrigger value="settings">Settings</TabsTrigger>
              </TabsList>

              <TabsContent value="receipts" className="mt-4 space-y-4">
                <ReceiptTray
                  userId={id!}
                  uploadFiles={uploadFiles}
                  setUploadFiles={setUploadFiles}
                  uploadFile={uploadFile}
                  // Only expose the "Upload more" button when the bulk
                  // upload area is NOT already on screen. When it IS open,
                  // clicking the button would be a no-op and feel broken.
                  onUploadMore={
                    showUpload ? undefined : () => setShowUpload(true)
                  }
                />
                <ConfirmedReceiptsTable
                  userId={id!}
                  categories={accountant?.categories ?? []}
                  clientName={clientName}
                  clientEmail={client.invite_email}
                  countryConfig={countryConfig}
                  onPeriodChange={(preset, from, to) => {
                    setReceiptsPeriod(preset);
                    setReceiptsFrom(from);
                    setReceiptsTo(to);
                  }}
                />
              </TabsContent>

              <TabsContent value="messages" className="mt-4">
                <MessagesTab userId={id!} />
              </TabsContent>

              <TabsContent value="settings" className="mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">
                      Connection settings
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {!formSeeded ? (
                      <>
                        <Skeleton className="h-8 w-full" />
                        <Skeleton className="h-8 w-full" />
                        <Skeleton className="h-8 w-24" />
                      </>
                    ) : (
                    <><div className="space-y-1.5">
                      <Label htmlFor={displayNameId} className="text-xs">Display name</Label>
                      <Input
                        id={displayNameId}
                        value={settingsForm.display_name}
                        onChange={(e) =>
                          setSettingsForm((f) => ({
                            ...f,
                            display_name: (e.target as HTMLInputElement).value,
                          }))
                        }
                      />
                    </div>
                    <div
                      className={`grid gap-4 ${isAustralia(countryConfig) && BAS_APPLICABLE_ENTITIES.includes(settingsForm.entity_type) ? "grid-cols-2" : "grid-cols-1"}`}
                    >
                      <div className="space-y-1.5">
                        {/* D2f — entity type shows label not raw value */}
                        <Label id={entityTypeLabelId} className="text-xs">Entity type</Label>
                        <Select
                          value={settingsForm.entity_type}
                          onValueChange={(v) =>
                            setSettingsForm((f) => ({
                              ...f,
                              entity_type: v ?? "",
                              ...(!BAS_APPLICABLE_ENTITIES.includes(v ?? "")
                                ? { bas_frequency: "" }
                                : {}),
                            }))
                          }
                        >
                          <SelectTrigger aria-labelledby={entityTypeLabelId} aria-label="Entity type">
                            <SelectValue placeholder="Choose entity type">
                              {entityTypeLabel(
                                countryConfig,
                                settingsForm.entity_type,
                              ) || "Choose entity type"}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {entityTypes(countryConfig).map((t) => (
                              <SelectItem key={t.value} value={t.value}>
                                {t.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {isAustralia(countryConfig) &&
                        BAS_APPLICABLE_ENTITIES.includes(
                          settingsForm.entity_type,
                        ) && (
                        <div className="space-y-1.5">
                          {/* D2f — BAS frequency shows label not raw value */}
                          <Label id={basFrequencyLabelId} className="text-xs">BAS frequency</Label>
                          <Select
                            value={settingsForm.bas_frequency}
                            onValueChange={(v) =>
                              setSettingsForm((f) => ({
                                ...f,
                                bas_frequency: v ?? "",
                              }))
                            }
                          >
                            <SelectTrigger aria-labelledby={basFrequencyLabelId} aria-label="BAS frequency">
                              <SelectValue placeholder="Choose BAS frequency">
                                {BAS_OPTIONS.find(
                                  (o) => o.value === settingsForm.bas_frequency,
                                )?.label ?? "Choose BAS frequency"}
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              {BAS_OPTIONS.map((o) => (
                                <SelectItem key={o.value} value={o.value}>
                                  {o.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                    <Button
                      size="sm"
                      onClick={() => updateSettingsMutation.mutate()}
                      disabled={updateSettingsMutation.isPending}
                    >
                      <Save className="mr-1.5 size-3" />
                      {updateSettingsMutation.isPending
                        ? "Saving..."
                        : "Save changes"}
                    </Button>
                    </>)}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Right sidebar */}
          <aside className="hidden xl:block w-72 shrink-0 space-y-4">
            {/* D2c — client summary with name */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-medium uppercase text-muted-foreground tracking-wider">
                  Client summary
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2.5 text-sm">
                {/* D2c — show name prominently */}
                <div>
                  <p className="font-semibold">{clientName}</p>
                  {client.display_name && (
                    <p className="text-muted-foreground text-xs truncate">
                      {client.invite_email}
                    </p>
                  )}
                </div>
                {client.entity_type && (
                  <div>
                    <p className="text-muted-foreground text-xs">Entity type</p>
                    <Badge variant="outline" className="mt-0.5">
                      {entityTypeLabel(countryConfig, client.entity_type)}
                    </Badge>
                  </div>
                )}
                <div>
                  <p className="text-muted-foreground text-xs">Subscription</p>
                  <SubscriptionBadge
                    status={client.subscription_status}
                    expiresAt={client.subscription_expires_at}
                  />
                  {client.subscription_status === "cancelled" &&
                    client.subscription_expires_at && (
                      <p className="text-muted-foreground text-xs mt-1">
                        Access ends{" "}
                        {new Date(
                          client.subscription_expires_at,
                        ).toLocaleDateString(undefined, {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })}
                      </p>
                    )}
                </div>
                {/* D2d — receipt count */}
                <div>
                  <p className="text-muted-foreground text-xs">Receipts</p>
                  <p className="font-medium">{confirmedReceiptCount}</p>
                </div>
              </CardContent>
            </Card>

            {/* D2a — Current period card: tax return always + BAS only if applicable */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-medium uppercase text-muted-foreground tracking-wider">
                  Current period
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-3">
                {/* Tax return — always shown */}
                <div className="rounded-lg border p-3">
                  <div>
                    <p className="text-sm font-medium">{fy.label}</p>
                    <p className="text-xs text-muted-foreground">
                      {fy.start} to {fy.end}
                    </p>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {currentFYReceiptCount} receipts in this {fy.noun}
                  </p>
                </div>

                {/* BAS — only if applicable */}
                {showBAS && (
                  <div className="rounded-lg border p-3">
                    <div>
                      <p className="text-sm font-medium">
                        {client.bas_frequency === "annual"
                          ? `Annual BAS ${fy.label.replace("Tax return ", "")}`
                          : currentQuarterInfo.label}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {client.bas_frequency === "annual"
                          ? `${fy.start} to ${fy.end}`
                          : `${currentQuarterInfo.start} to ${currentQuarterInfo.end}`}
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Tray in the main content area is now the single source of
                truth for in-flight receipts; no duplicate sidebar card. */}
          </aside>
        </div>
      )}

      {/* Reminder dialog */}
      {client && (
        <ReminderDialog
          open={reminderOpen}
          onOpenChange={setReminderOpen}
          target={{ kind: "single", id: client.id, name: clientName }}
          countryConfig={countryConfig}
        />
      )}

      {/* Export dialog */}
      {client && (
        <ExportDialog
          open={exportOpen}
          onOpenChange={setExportOpen}
          target={{
            kind: "single",
            clientId: client.id,
            clientName: clientName,
            clientEmail: client.invite_email,
          }}
          defaultPeriod={receiptsPeriod}
          defaultFrom={receiptsFrom ?? undefined}
          defaultTo={receiptsTo ?? undefined}
          countryConfig={countryConfig}
        />
      )}

      {/* Confirm dialogs */}
      <ConfirmDialog
        open={confirmKind === "disconnect"}
        onOpenChange={(o) => !o && setConfirmKind(null)}
        title={`Disconnect ${clientName}?`}
        description="They'll keep their receipts but won't appear in your client list. You can re-invite them anytime."
        confirmLabel="Disconnect"
        destructive
        loading={disconnectMutation.isPending}
        onConfirm={() => disconnectMutation.mutate()}
      />
      <ReinviteDialog
        open={confirmKind === "reinvite"}
        onOpenChange={(o) => !o && setConfirmKind(null)}
        clientId={id!}
        clientName={clientName}
        clientEmail={client?.invite_email}
        accountantName={
          accountant?.business_name || accountant?.email || undefined
        }
        loading={reinviteMutation.isPending}
        onConfirm={(note, subject) => {
          reinviteMutation.mutate(
            { personal_note: note, subject },
            { onSuccess: () => setConfirmKind(null) },
          );
        }}
      />
    </div>
  );
}
